import { z } from 'zod';
import type { WireSourceField, WireSourceInstance, WireSourceKind } from '@logistics/contracts';

/**
 * The add/edit source form's schema, built from the plugin's descriptor: the
 * panel knows nothing about Samsara, only that `apiToken` is a required
 * secret. Every value stays a string — the API coerces it with the same
 * descriptor — so this only catches what would certainly be refused.
 */
export interface SourceFormValues {
  readonly label: string;
  readonly fields: Record<string, string>;
}

const LABEL_MAX = 120;

export function sourceFormSchema(
  descriptor: WireSourceKind,
  existing?: Pick<WireSourceInstance, 'secretsPresent'>,
): z.ZodType<SourceFormValues, SourceFormValues> {
  const fields: Record<string, z.ZodType<string, string>> = {};
  for (const field of descriptor.fields) {
    const stored = field.secret && (existing?.secretsPresent.includes(field.name) ?? false);
    fields[field.name] = fieldSchema(field, stored);
  }
  return z.object({
    label: z.string().trim().max(LABEL_MAX, `Display name is at most ${LABEL_MAX} characters.`),
    fields: z.object(fields),
  });
}

/**
 * One field. A stored secret may stay blank — blank means "keep it" — so it is
 * never required on edit. The messages are the design's: "API token is required."
 */
function fieldSchema(field: WireSourceField, stored: boolean): z.ZodType<string, string> {
  const required = field.required && !stored;
  const base = z.string().trim();
  const present = required ? base.min(1, `${field.label} is required.`) : base;
  switch (field.type) {
    case 'number':
      return present.refine(
        (v) => v === '' || Number.isFinite(Number(v)),
        `${field.label} must be a number.`,
      );
    case 'boolean':
      return present.refine(
        (v) => v === '' || v === 'true' || v === 'false',
        `${field.label} is on or off.`,
      );
    case 'select': {
      const options = field.options ?? [];
      // The list is a suggestion when anything else may be typed in.
      if (field.allowCustom === true) return present;
      return present.refine(
        (v) => v === '' || options.includes(v),
        `${field.label} must be one of: ${options.join(', ')}.`,
      );
    }
    case 'text':
    case 'secret':
      return present;
  }
}

/** A setting prefills; a secret never does — the panel is never sent one. */
export function initialSourceValues(
  descriptor: WireSourceKind,
  existing?: WireSourceInstance,
): SourceFormValues {
  const fields: Record<string, string> = {};
  for (const f of descriptor.fields) {
    fields[f.name] = f.secret
      ? ''
      : (asText(existing?.settings[f.name]) ?? asText(f.default) ?? firstOption(f) ?? '');
  }
  return { label: existing?.label ?? '', fields };
}

function firstOption(field: WireSourceField): string | undefined {
  return field.type === 'select' && field.required ? field.options?.[0] : undefined;
}

function asText(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
}

/** The single error line the design shows: the first problem, in field order. */
export function firstSourceError(
  descriptor: WireSourceKind,
  errors: {
    label?: { message?: string | undefined } | undefined;
    fields?: Partial<Record<string, { message?: string | undefined } | undefined>> | undefined;
  },
): string | undefined {
  if (errors.label?.message !== undefined) return errors.label.message;
  for (const field of descriptor.fields) {
    const message = errors.fields?.[field.name]?.message;
    if (message !== undefined) return message;
  }
  return undefined;
}
